import React, {useEffect, useState} from 'react';
import SwiperCore, {A11y, Autoplay, Navigation, Pagination, Scrollbar} from 'swiper';
import {Swiper, SwiperSlide} from 'swiper/react';
import UserService from '../../services/user.service';
SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Autoplay]);
import 'swiper/swiper-bundle.min.css';
import {getLocaleContent} from '@helper/locale-content.js';
import {useRouter} from 'next/router';
import Link from 'next/link';
import {changeToSlug} from '@helper/string.helper';
import styles from '../SpecialistDoctor/RelatedDoctors.module.css';
import {useBooking} from '@contexts/BookingContext';
import {safeHtml} from '@helper/sanitize-html';

/**
 * Component hiển thị danh sách bác sĩ của bệnh viện
 * Cho phép user click "Đặt lịch khám" để tự động fill form
 */
export default function MedicalFacilityDoctors(props) {
    const [users, setUsers] = useState([]);
    const {locale} = useRouter();
    const {setBookingInfo} = useBooking();

    // Lấy business_id từ props một cách ổn định
    const medicalFacility = Array.isArray(props?.preloadedMedicalFacility)
        ? props.preloadedMedicalFacility[0]
        : props?.preloadedMedicalFacility;
    const businessId = medicalFacility?.business_id;

    useEffect(() => {
        if (!businessId) {
            setUsers([]);
            return;
        }

        // Reset users khi business_id thay đổi để tránh hiển thị data cũ
        setUsers([]);

        const userService = new UserService();
        userService
            .getUserList({
                locale: locale,
                pageSize: 100,
                pageIndex: 1,
                businessId: businessId,
            })
            .then(response => {
                const items = response?.data?.items || response?.items;
                if (items && Array.isArray(items) && items.length > 0) {
                    setUsers(items);
                } else {
                    setUsers([]);
                }
            })
            .catch(error => {
                console.error('Lỗi khi lấy danh sách bác sĩ:', error);
                setUsers([]);
            });
    }, [businessId, locale]);

    const generateSlug = user => {
        const nameSlug = changeToSlug(user?.full_name);
        const id = user?.user_id;
        if (id) {
            return `${nameSlug}-u${id}`;
        }
        return '';
    };
    // Cấu hình Swiper cho slider bác sĩ
    const swiperConfig = {
        autoplay:
            users.length >= 3
                ? {
                      delay: 2500,
                      disableOnInteraction: false,
                      pauseOnMouseEnter: true,
                  }
                : false,
        loop: users.length >= 3,
        spaceBetween: 14,
        navigation: {
            nextEl: `.${styles['next-button']}`,
            prevEl: `.${styles['previous-button']}`,
        },
        watchOverflow: true,
        observer: true,
        observeParents: true,
        slidesPerView: 'auto',
        onSwiper: swiper => {
            // Setup autoplay restart khi click navigation buttons
            if (users.length >= 3 && swiper.autoplay) {
                const restart = () => {
                    if (swiper.autoplay && swiper.autoplay.running === false) {
                        swiper.autoplay.start();
                    }
                };
                const nextBtn = document.querySelector(`.${styles['next-button']}`);
                const prevBtn = document.querySelector(`.${styles['previous-button']}`);
                if (nextBtn) {
                    nextBtn.addEventListener('click', () => setTimeout(restart, 50));
                }
                if (prevBtn) {
                    prevBtn.addEventListener('click', () => setTimeout(restart, 50));
                }
            }
        },
    };

    if (!users?.length) {
        return null;
    }

    return (
        <>
            <section className="doctors">
                <div className="container">
                    <header className="doctors__head">
                        <h2 className="doctors__title">
                            {getLocaleContent(locale, 'page.medical-facility.doctors-title-part1')}{' '}
                            <span>{getLocaleContent(locale, 'page.medical-facility.doctors-title-part2')}</span>{' '}
                            {medicalFacility?.business_name || ''}
                        </h2>
                        <p className="doctors__lead">{getLocaleContent(locale, 'page.doctors.lead')}</p>
                    </header>
                    <div className={styles['swiper-container']}>
                        <button
                            className={`${styles['previous-button']} navbtn bw_hover_next`}
                            aria-label="Prev"></button>
                        <button className={`${styles['next-button']} navbtn bw_hover_prev`} aria-label="Next"></button>

                        <div className={styles['doctor-carousel']}>
                            <Swiper {...swiperConfig} className={styles['swiper']}>
                                {users
                                    .filter(user => user && user.user_id)
                                    .map((user, index) => (
                                        <SwiperSlide
                                            key={user.user_id || index}
                                            style={{
                                                width: '317px',
                                                minWidth: '317px',
                                                flexShrink: 0,
                                            }}>
                                            <Link
                                                href={{
                                                    pathname: '/[slug]',
                                                    query: {slug: encodeURIComponent(generateSlug(user))},
                                                }}>
                                                <article className="doccard">
                                                    <div className="doccard__media">
                                                        <img src={user.image_url} alt={user.full_name} />
                                                    </div>
                                                    <div className="doccard__body">
                                                        <span className="doccard__tag">
                                                            {user.education_level_name || ' '}
                                                        </span>
                                                        <h3 className="doccard__name">{user.full_name}</h3>
                                                        <p className="doccard__meta">
                                                            {user.position_name}
                                                            {' - '}
                                                            {user.department_name}
                                                            {user?.business_name && (
                                                                <>
                                                                    <br />
                                                                    {user.business_name}
                                                                </>
                                                            )}
                                                            {user?.expertise && (
                                                                <>
                                                                    <br />
                                                                    {getLocaleContent(
                                                                        locale,
                                                                        'page.doctors.expertise',
                                                                    )}{' '}
                                                                    {user.expertise}
                                                                </>
                                                            )}
                                                        </p>
                                                        <button
                                                            className="doccard__cta"
                                                            onClick={e => {
                                                                e.preventDefault();
                                                                e.stopPropagation();

                                                                // Set dữ liệu đặt lịch cho bác sĩ
                                                                console.log(
                                                                    '[MedicalFacilityDoctors] Click đặt lịch bác sĩ:',
                                                                    {
                                                                        user_id: user.user_id,
                                                                        full_name: user.full_name,
                                                                        department_id: user.department_id,
                                                                        department_name: user.department_name,
                                                                        business_id:
                                                                            medicalFacility?.business_id ||
                                                                            user.business_id,
                                                                    },
                                                                );
                                                                setBookingInfo({
                                                                    hospital: medicalFacility?.business_name || '',
                                                                    business_id:
                                                                        medicalFacility?.business_id ||
                                                                        user.business_id ||
                                                                        '',
                                                                    department: user.department_name || '',
                                                                    department_id: user.department_id || '',
                                                                    doctor: user.full_name || '',
                                                                    user_id: user.user_id || '',
                                                                    user_name: user.user_name || '',
                                                                });

                                                                // Scroll xuống form đặt lịch
                                                                const formSection =
                                                                    document.querySelector('.register-section');
                                                                if (formSection) {
                                                                    const offset = 150; // Khoảng cách từ top
                                                                    const elementPosition = formSection.offsetTop;
                                                                    const offsetPosition = elementPosition - offset;

                                                                    window.scrollTo({
                                                                        top: offsetPosition,
                                                                        behavior: 'instant',
                                                                    });
                                                                }
                                                            }}>
                                                            <img
                                                                className="icon"
                                                                src="/bw_image/ic_calendar_w.png"
                                                                alt=""
                                                            />
                                                            {getLocaleContent(locale, 'page.doctors.booking')}
                                                        </button>
                                                    </div>
                                                    <div className="doccard__overlay">
                                                        <div className="doccard__ov-wrap">
                                                            {user.education_level_name && (
                                                                <span className="doccard__tag">
                                                                    {user.education_level_name}
                                                                </span>
                                                            )}
                                                            <h3 className="doccard__name">{user.full_name}</h3>
                                                            <p className="doccard__meta">
                                                                {user.position_name} {' - '} {user.department_name}
                                                            </p>
                                                            <p className="doccard__meta">
                                                                {user?.business_name && <>{user.business_name}</>}
                                                                {user?.expertise && (
                                                                    <>
                                                                        <br />
                                                                        {getLocaleContent(
                                                                            locale,
                                                                            'page.doctors.expertise',
                                                                        )}{' '}
                                                                        {user.expertise}
                                                                    </>
                                                                )}
                                                            </p>
                                                            <h4 className="doccard__overlay-title">
                                                                {getLocaleContent(
                                                                    locale,
                                                                    'page.doctors.experience-department',
                                                                )}
                                                            </h4>
                                                            <div
                                                                className="doccard__list"
                                                                dangerouslySetInnerHTML={safeHtml(
                                                                    user.about_me_short,
                                                                )}></div>
                                                            <button
                                                                className="doccard__cta"
                                                                onClick={e => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();

                                                                    // Set dữ liệu đặt lịch cho bác sĩ (overlay)
                                                                    console.log(
                                                                        '[MedicalFacilityDoctors] Click đặt lịch bác sĩ (overlay):',
                                                                        {
                                                                            user_id: user.user_id,
                                                                            full_name: user.full_name,
                                                                            department_id: user.department_id,
                                                                            department_name: user.department_name,
                                                                            business_id:
                                                                                medicalFacility?.business_id ||
                                                                                user.business_id,
                                                                        },
                                                                    );
                                                                    setBookingInfo({
                                                                        hospital: medicalFacility?.business_name || '',
                                                                        business_id:
                                                                            medicalFacility?.business_id ||
                                                                            user.business_id ||
                                                                            '',
                                                                        department: user.department_name || '',
                                                                        department_id: user.department_id || '',
                                                                        doctor: user.full_name || '',
                                                                        user_id: user.user_id || '',
                                                                        user_name: user.user_name || ''
                                                                    });

                                                                    // Scroll xuống form đặt lịch
                                                                    const formSection =
                                                                        document.querySelector('.register-section');
                                                                    if (formSection) {
                                                                        const offset = 150; // Khoảng cách từ top
                                                                        const elementPosition = formSection.offsetTop;
                                                                        const offsetPosition = elementPosition - offset;

                                                                        window.scrollTo({
                                                                            top: offsetPosition,
                                                                            behavior: 'instant',
                                                                        });
                                                                    }
                                                                }}>
                                                                <img
                                                                    className="icon"
                                                                    src="/bw_image/ic_calendar_w.png"
                                                                    alt=""
                                                                />
                                                                {getLocaleContent(locale, 'page.doctors.booking')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </article>
                                            </Link>
                                        </SwiperSlide>
                                    ))}
                            </Swiper>
                        </div>
                    </div>

                    <div className="doctors__more">
                        <Link href={`/chuyen-gia-bac-si/${medicalFacility?.business_id}`}>
                            <a className="svc__all">
                                {getLocaleContent(locale, 'common.see-all')}
                                <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
                                    <path
                                        d="M8 5l8 7-8 7"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </a>
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
